import * as THREE from 'three';

import { deriveNaturalLessThanTrace } from '@/adapters/primitive/_pure/model/natural/comparison';
import { toNaturalDigitAngle } from '@/adapters/primitive/_pure/derivation/constellations';
import { buildBooleanRodState, toBooleanWheelPositions } from '@/adapters/primitive/_pure/derivation/boolean/traceProjection';
import { rotateBooleanSection, rotateBooleanSectionLateral } from '@/adapters/primitive/_pure/derivation/boolean/geometry';
import { buildBooleanPlaceholderWheel, toWheelLayout, toWheelPosition } from '@/adapters/primitive/_pure/derivation/wheels';
import { momentKey, stepMoment } from '@/adapters/primitive/_pure/derivation/primitiveMoment';
import { selectPrimitiveDirectionEffects } from '@/adapters/primitive/_pure/derivation/primitiveDirectionRules';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { PrimitiveOperationDirectionEffect } from '@/adapters/primitive/_contract/_~generated/operationDirectionEffect';
import type { BooleanRodState } from '@/adapters/primitive/_contract/boolean/rod';
import type { NaturalLessThanTrace } from '@/adapters/primitive/_contract/natural/comparison';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveKeyframe, PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

export function deriveNaturalLessThanStepStates(args: {
    config: Config;
    source: number;
    target: number;
}): PrimitiveStepState[] {
    return projectNaturalLessThanTrace({ ...args, trace: deriveNaturalLessThanTrace({
        source: args.source, target: args.target, radix: args.config.natural.radix,
    }) });
}

export function projectNaturalLessThanTrace(args: {
    config: Config;
    source: number;
    target: number;
    trace: NaturalLessThanTrace;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
}): PrimitiveStepState[] {
    if (args.config.natural.display.comparison.naturalLessThanPresentationMode === 'boolean-channel') {
        return projectNaturalLessThanBooleanChannel(args);
    }
    const { config, source, target } = args;
    const trace = args.trace;
    const layout = toWheelLayout({ config, operandSchedulingMode: config.natural.operandSchedulingMode });
    const timing = config.natural.animation?.naturalLessThan;
    const center = toWheelPosition({ layout, role: 'less-than-decision' });
    const radius = config.base.display.wheel.innerRadius
        + (trace.powers[0] ?? 0) * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap)
        + config.base.display.wheel.ringWidth;
    const inspected = { source: new Map<number, number[]>(), target: new Map<number, number[]>() };
    let scanPower = trace.powers[0] ?? 0;
    let scanSlot = 1;
    let angle = toNaturalDigitAngle(config, scanSlot);
    let position: [number, number, number] = [...center];
    let reach: 'target' | 'source' = 'target';
    let sourceReachProgress = 1;
    let raysVisible = false;
    let orbitVisible = false;
    let decision: boolean | undefined;
    let growth = 0;
    let operandOpacity = 1;
    let decisiveCell: { side: 'source' | 'target'; power: number; digit: number } | undefined;
    const inputWheel = (side: 'source' | 'target', value: number): PrimitiveWheelState => ({
        role: side === 'source' ? 'less-than-source' : 'less-than-target',
        value,
        wheelOpacity: operandOpacity,
        powers: [...trace.powers].reverse(),
        position: toWheelPosition({ layout, role: side === 'source' ? 'less-than-source' : 'less-than-target' }),
        countedThroughDigitByPower: Object.fromEntries(trace.powers.map((power) => [
            power, Math.floor(value / config.natural.radix ** power) % config.natural.radix,
        ])),
        cellOverridesByPower: Object.fromEntries([...inspected[side]].map(([power, slots]) => [
            power, Object.fromEntries(slots.map((slot) => [slot, { opacity: timing?.inspectedCellOpacity ?? 0.12 }])),
        ])),
        ...(decisiveCell?.side === side && selectPrimitiveDirectionEffects({
            moment: stepMoment('compare-power', { power: scanPower }), role: 'decisive-cell',
        }, args.directionCatalog).some(effect => effect.visual === 'comparison-decisive-cell')
            ? { comparisonDecisiveCell: { power: decisiveCell.power, digit: decisiveCell.digit } } : {}),
    });
    const snapshot = (durationMs?: number): PrimitiveKeyframe => ({
        wheels: [
            inputWheel('source', source),
            inputWheel('target', target),
            {
                ...(decision === undefined
                    ? buildBooleanPlaceholderWheel({ layout, role: 'less-than-decision' })
                    : { valueKind: 'boolean' as const, role: 'less-than-decision' as const, value: decision, powers: [0], discGrowth: growth }),
                position: [...position],
                comparisonScanPower: scanPower,
                comparisonScanSlot: scanSlot,
                comparisonRaysVisible: raysVisible,
                comparisonRayReach: reach,
                comparisonSourceReachProgress: sourceReachProgress,
                ...(orbitVisible ? { comparisonOrbitRadius: radius, comparisonOrbitCenter: center } : {}),
            },
        ],
        transition: durationMs === undefined || durationMs === 0 ? 'instant' : { durationMs },
        events: [],
    });
    const steps: PrimitiveStepState[] = [{
        ...momentKey(stepMoment('initial')), label: 'Start',
        description: 'The unresolved LessThanDecision waits behind the operands.',
        keyframes: [snapshot()],
    }];
    orbitVisible = true;
    const guideEntry = snapshot();
    position = [radius * Math.sin(angle), -radius * Math.cos(angle), center[2]];
    steps.push({
        ...momentKey(stepMoment('eye-entry')), label: 'Position Eye',
        description: 'Move the decision hub onto the scanning circle behind Target.',
        keyframes: [guideEntry, snapshot(timing?.helperEntryDurationMs ?? 0)],
    });
    const burn = (side: 'source' | 'target') => {
        inspected[side].set(scanPower, [...(inspected[side].get(scanPower) ?? []), scanSlot]);
    };
    for (const observation of trace.observations) {
        scanPower = observation.power;
        const keyframes: PrimitiveKeyframe[] = [];
        for (const slot of observation.slots) {
            scanSlot = slot.slot;
            raysVisible = false;
            const nextAngle = toNaturalDigitAngle(config, scanSlot);
            const orbitAngle = nextAngle - angle;
            angle = nextAngle;
            position = [radius * Math.sin(angle), -radius * Math.cos(angle), center[2]];
            keyframes.push({
                ...snapshot(),
                transition: { durationMs: timing?.slotScanDurationMs ?? 0, comparisonEyeOrbitAngle: orbitAngle },
            });
            reach = 'target';
            raysVisible = true;
            keyframes.push(snapshot());
            burn('target');
            keyframes.push(snapshot(timing?.targetObservationDurationMs ?? 0));
            reach = 'source';
            sourceReachProgress = 0;
            keyframes.push(snapshot());
            sourceReachProgress = 1;
            keyframes.push(snapshot((timing?.sourceObservationDurationMs ?? 0) / 2));
            burn('source');
            keyframes.push(snapshot((timing?.sourceObservationDurationMs ?? 0) / 2));
        }
        const terminal = observation.relation !== 'equal' || observation === trace.observations.at(-1);
        if (terminal) {
            if (observation.relation !== 'equal') {
                decisiveCell = { side: observation.relation === 'less' ? 'source' : 'target', power: scanPower, digit: scanSlot };
                keyframes.push(snapshot());
            }
            keyframes.push(snapshot(timing?.decisiveCellFlashDurationMs ?? 0));
            decisiveCell = undefined;
            raysVisible = false;
            decision = trace.decision;
            keyframes.push(snapshot());
            growth = 1;
            keyframes.push(snapshot(timing?.decisionMaterializationDurationMs ?? 0));
        } else {
            raysVisible = false;
            keyframes.push(snapshot(timing?.bandAdvanceDurationMs ?? 0));
        }
        steps.push({
            ...momentKey(stepMoment('compare-power', { power: observation.power })),
            label: terminal ? 'Decide Less Than' : `Compare ${config.natural.radix}^${observation.power}`,
            description: terminal
                ? `${observation.sourceDigit} is ${observation.relation} ${observation.targetDigit}; LessThan is ${trace.decision}.`
                : `${observation.sourceDigit} equals ${observation.targetDigit}; continue inward.`,
            keyframes,
        });
    }
    position = [...center];
    const returned = snapshot(timing?.helperEntryDurationMs ?? 0);
    orbitVisible = false;
    const landed = snapshot();
    operandOpacity = config.natural.display.band.spentOpacity;
    steps.push({
        ...momentKey(stepMoment('eye-return')), label: 'Return Decision',
        description: 'Return the materialized Boolean to its output position, then dim the remaining operands.',
        keyframes: [returned, landed, snapshot(config.natural.animation?.fadeDurationMs ?? 0)],
    });
    return steps;
}

export function projectNaturalLessThanBooleanChannel(args: {
    config: Config;
    source: number;
    target: number;
    trace: NaturalLessThanTrace;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
    positions?: ReturnType<typeof toBooleanWheelPositions>;
}): PrimitiveStepState[] {
    const { config, source, target, trace } = args;
    const layout = toWheelLayout({ config, operandSchedulingMode: config.natural.operandSchedulingMode });
    const timing = config.natural.animation?.naturalLessThan;
    const positions = args.positions ?? toBooleanWheelPositions(config, 'logical-xor');
    const inspected = { source: new Map<number, number[]>(), target: new Map<number, number[]>() };
    type Side = 'source' | 'target';
    type Position = [number, number, number];
    type Profile = NonNullable<BooleanRodState['segments'][number]['profile']>;
    type RodSegment = { from: Position; to: Position; lateral: Position; color: string; opacity: number; profile: Profile };
    const operands: Partial<Record<Side, RodSegment>> = {};
    const travellers: Partial<Record<Side, RodSegment>> = {};
    const poses: Record<Side, { position: Position; rotation: Position }> = {
        source: { position: [...positions.inputOne], rotation: [Math.PI / 2, 0, 0] },
        target: { position: [...positions.inputTwo], rotation: [Math.PI / 2, 0, 0] },
    };
    const presented: Partial<Record<Side, { power: number; slot: number }>> = {};
    let mismatchChannel: 'near' | 'far' | undefined;
    let decision: boolean | undefined;
    let decisionGrowth = 0;
    let operandOpacity = 1;

    const color = (counted: boolean) => counted
        ? config.base.display.cell.countedColor
        : config.base.display.cell.uncountedColor;
    const declared = buildBooleanRodState({
        config, operation: 'logical-xor', inputOne: false, inputTwo: true, positions,
    });
    const segment = (key: string) => declared.segments.find((candidate) => candidate.segmentKey === `mixed:${key}`)!;
    const junction = segment('output-far').from as Position;
    const midpoint = segment('output-far').to as Position;
    const outputEnd = segment('output-near').to as Position;
    const operandTemplate = (side: Side): BooleanRodState['segments'][number] =>
        segment(side === 'source' ? 'input-one' : 'input-two');
    const operandFor = (side: Side): RodSegment => {
        const template = operandTemplate(side);
        const profile = template.profile;
        if (!profile) throw new Error('Boolean operand requires the native bar profile.');
        return {
            from: [...template.from] as Position,
            to: [...template.to] as Position,
            lateral: [...profile.lateral],
            color: config.base.display.placeholder.outlineColor,
            opacity: 1,
            profile,
        };
    };
    operands.source = operandFor('source');
    operands.target = operandFor('target');
    const contactPose = (side: Side, power: number, slot: number) => {
        const tilt = side === 'source' ? Math.PI / 3 : 2 * Math.PI / 3;
        const previous = presented[side];
        // Preserve one-slot, unwrapped Y turns instead of taking an Euler/quaternion shortcut.
        const spin = previous?.power === power
            ? poses[side].rotation[1] - (toNaturalDigitAngle(config, slot) - toNaturalDigitAngle(config, previous.slot))
            : -toNaturalDigitAngle(config, slot);
        const rotation: Position = [tilt, spin, 0];
        const radius = config.base.display.wheel.innerRadius
            + power * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap)
            + config.base.display.wheel.ringWidth / 2;
        const radial = new THREE.Vector3(0, 0, radius).applyEuler(new THREE.Euler(tilt, 0, 0, 'XYZ'));
        const contact = new THREE.Vector3(...operandTemplate(side).from);
        poses[side] = { position: contact.sub(radial).toArray() as Position, rotation };
        presented[side] = { power, slot };
    };
    const rotateLateral = (
        side: Side,
        from: Position,
        to: Position,
        pivot: Position,
        lateral?: Position,
    ): Position => {
        const template = operandTemplate(side);
        return rotateBooleanSectionLateral({
            pivot,
            from,
            to,
            progress: 1,
            lateral: lateral ?? template.profile?.lateral ?? [1, 0, 0],
        });
    };
    const travelling = (side: Side, from: Position, to: Position, lateral: Position): RodSegment => ({
        ...operands[side]!,
        from,
        to,
        lateral,
        opacity: 1,
        profile: { ...operands[side]!.profile, lateral },
    });
    const booleanRod = (): BooleanRodState => {
        const power = presented.source?.power;
        if (mismatchChannel !== undefined && power === undefined) throw new Error('Comparison mismatch requires a presented Source cell.');
        const outline = mismatchChannel !== undefined && selectPrimitiveDirectionEffects({
            moment: stepMoment('compare-power', { power }), role: 'mismatch',
        }, args.directionCatalog).some(effect => effect.visual === 'comparison-mismatch-outline');
        const segments = declared.segments.map((current) => {
            const section = current.segmentKey.split(':')[1];
            if (section === 'input-one' || section === 'input-two') {
                return { ...current, ...operands[section === 'input-one' ? 'source' : 'target']! };
            }
            if (section !== 'output-near' && section !== 'output-far') return current;
            const channel = section === 'output-near' ? 'near' : 'far';
            const required = channel === 'far';
            return {
                ...current,
                color: color(required),
                ...(current.profile ? { profile: {
                    ...current.profile,
                    border: { ...config.boolean.display.decisiveOutline, opacity: outline && mismatchChannel === channel ? 1 : 0 },
                } } : {}),
            };
        });
        for (const side of ['source', 'target'] as const) {
            if (travellers[side]) segments.push({ segmentKey: `traveller:${side}`, ...travellers[side]! });
        }
        return { segments };
    };
    const inputWheel = (side: 'source' | 'target', value: number): PrimitiveWheelState => ({
        role: side === 'source' ? 'less-than-source' : 'less-than-target',
        value,
        wheelOpacity: operandOpacity,
        powers: [...trace.powers].reverse(),
        position: [...poses[side].position],
        wheelRotation: [...poses[side].rotation],
        bandLabels: { radial: false, angular: false },
        countedThroughDigitByPower: Object.fromEntries(trace.powers.map((power) => [
            power, Math.floor(value / config.natural.radix ** power) % config.natural.radix,
        ])),
        cellOverridesByPower: Object.fromEntries([...inspected[side]].map(([power, slots]) => [
            power, Object.fromEntries(slots.map((slot) => [slot, { opacity: timing?.inspectedCellOpacity ?? 0.12 }])),
        ])),
    });
    const snapshot = (durationMs?: number): PrimitiveKeyframe => ({
        wheels: [
            inputWheel('source', source),
            inputWheel('target', target),
            {
                ...(decision === undefined
                    ? buildBooleanPlaceholderWheel({ layout, role: 'less-than-decision' })
                    : { valueKind: 'boolean' as const, role: 'less-than-decision' as const, value: decision, powers: [0], discGrowth: decisionGrowth }),
                position: [...positions.output],
            },
        ],
        booleanRod: booleanRod(),
        transition: durationMs === undefined || durationMs === 0 ? 'instant' : { durationMs },
        events: [],
    });
    const burn = (side: Side, power: number, slot: number) => {
        inspected[side].set(power, [...(inspected[side].get(power) ?? []), slot]);
    };
    const steps: PrimitiveStepState[] = [{
        ...momentKey(stepMoment('initial')),
        label: 'Start',
        description: 'Declare Result-near=false and Result-far=true; original neutral operand segments face the operation hub.',
        keyframes: [snapshot()],
    }];
    const presentCell = (side: Side, power: number, slot: number, keyframes: PrimitiveKeyframe[]) => {
        const previous = presented[side];
        if (previous && previous.power !== power) {
            // Leave the contact plane before changing radius, then engage the same fixed endpoint.
            const offset = new THREE.Vector3(0, config.base.display.wheel.ringWidth, 0)
                .applyEuler(new THREE.Euler(poses[side].rotation[0], 0, 0, 'XYZ'));
            const displace = (direction: number) => {
                poses[side] = {
                    ...poses[side],
                    position: new THREE.Vector3(...poses[side].position).addScaledVector(offset, direction).toArray() as Position,
                };
            };
            displace(1);
            keyframes.push(snapshot((timing?.bandAdvanceDurationMs ?? 0) / 3));
            contactPose(side, power, slot);
            displace(1);
            keyframes.push(snapshot((timing?.bandAdvanceDurationMs ?? 0) / 3));
            contactPose(side, power, slot);
            keyframes.push(snapshot((timing?.bandAdvanceDurationMs ?? 0) / 3));
        } else {
            contactPose(side, power, slot);
            keyframes.push(snapshot(previous ? timing?.slotScanDurationMs ?? 0 : timing?.helperEntryDurationMs ?? 0));
        }
    };

    for (const [observationIndex, observation] of trace.observations.entries()) {
        const keyframes: PrimitiveKeyframe[] = [];
        for (const slot of observation.slots) {
            presentCell('source', observation.power, slot.slot, keyframes);
            burn('source', observation.power, slot.slot);
            operands.source!.color = color(slot.sourceCounted);
            keyframes.push(snapshot());
            keyframes.push(snapshot(timing?.sourceObservationDurationMs ?? 0));
            operands.source!.opacity = timing?.inspectedCellOpacity ?? 0.12;

            const sourceStart = operandTemplate('source').from as Position;
            travellers.source = { ...operands.source!, opacity: 1 };
            keyframes.push(snapshot());
            travellers.source = travelling('source', junction, sourceStart,
                operands.source!.lateral);
            keyframes.push(snapshot());
            for (let index = 1; index <= config.boolean.animation.pivotSegments; index += 1) {
                const progress = index / config.boolean.animation.pivotSegments;
                const endpoint = rotateBooleanSection({ pivot: junction, from: sourceStart, to: midpoint, progress });
                travellers.source = travelling('source', junction, endpoint,
                    rotateLateral('source', sourceStart, endpoint, junction));
                keyframes.push(snapshot(config.boolean.animation.pivotDurationMs / config.boolean.animation.pivotSegments));
            }
            const farChannelLateral = rotateLateral('source', sourceStart, midpoint, junction);
            travellers.source = travelling('source', midpoint, junction, farChannelLateral);
            keyframes.push(snapshot());
            for (let index = 1; index <= config.boolean.animation.pivotSegments; index += 1) {
                const progress = index / config.boolean.animation.pivotSegments;
                const endpoint = rotateBooleanSection({ pivot: midpoint, from: junction, to: outputEnd, progress });
                const lateral = rotateBooleanSectionLateral({
                    pivot: midpoint, from: junction, to: outputEnd, progress, lateral: farChannelLateral,
                });
                travellers.source = travelling('source', midpoint, endpoint, lateral);
                keyframes.push(snapshot(config.boolean.animation.pivotDurationMs / config.boolean.animation.pivotSegments));
            }

            if (slot.sourceCounted) {
                mismatchChannel = 'near';
                keyframes.push(snapshot(timing?.decisiveCellFlashDurationMs ?? 0));
            } else {
                presentCell('target', observation.power, slot.slot, keyframes);
                burn('target', observation.power, slot.slot);
                operands.target!.color = color(slot.targetCounted);
                keyframes.push(snapshot());
                keyframes.push(snapshot(timing?.targetObservationDurationMs ?? 0));
                operands.target!.opacity = timing?.inspectedCellOpacity ?? 0.12;
                const targetStart = operandTemplate('target').from as Position;
                travellers.target = { ...operands.target!, opacity: 1 };
                keyframes.push(snapshot());
                travellers.target = travelling('target', junction, targetStart,
                    operands.target!.lateral);
                keyframes.push(snapshot());
                for (let index = 1; index <= config.boolean.animation.pivotSegments; index += 1) {
                    const progress = index / config.boolean.animation.pivotSegments;
                    const endpoint = rotateBooleanSection({ pivot: junction, from: targetStart, to: midpoint, progress });
                    travellers.target = travelling('target', junction, endpoint,
                        rotateLateral('target', targetStart, endpoint, junction));
                    keyframes.push(snapshot(config.boolean.animation.pivotDurationMs / config.boolean.animation.pivotSegments));
                }
                if (!slot.targetCounted) {
                    mismatchChannel = 'far';
                    keyframes.push(snapshot(timing?.decisiveCellFlashDurationMs ?? 0));
                }
            }

            const mismatch = mismatchChannel !== undefined;
            if (mismatch) {
                travellers.source = { ...travellers.source!, opacity: 0 };
                if (travellers.target) travellers.target = { ...travellers.target, opacity: 0 };
                keyframes.push(snapshot(config.natural.animation?.fadeDurationMs ?? 0));
                delete travellers.source;
                delete travellers.target;
                operands.source!.opacity = 1;
                operands.target!.opacity = 1;
                operands.source!.color = config.base.display.placeholder.outlineColor;
                operands.target!.color = config.base.display.placeholder.outlineColor;
                mismatchChannel = undefined;
                keyframes.push(snapshot());
            }

            const finalSlot = slot === observation.slots.at(-1);
            const terminal = finalSlot && (observation.relation !== 'equal'
                || observationIndex === trace.observations.length - 1);
            if (terminal) {
                decision = trace.decision;
                keyframes.push(snapshot());
                decisionGrowth = 1;
                keyframes.push(snapshot(timing?.decisionMaterializationDurationMs ?? 0));
                break;
            }
            if (slot.outcome === 'equal') {
                operands.source!.color = config.base.display.placeholder.outlineColor;
                operands.target!.color = config.base.display.placeholder.outlineColor;
                operands.source!.opacity = 1;
                operands.target!.opacity = 1;
                keyframes.push(snapshot());
                break;
            }
        }
        if (decision === undefined) keyframes.push(snapshot(timing?.bandAdvanceDurationMs ?? 0));
        steps.push({
            ...momentKey(stepMoment('compare-power', { power: observation.power })),
            label: observation.relation === 'equal' && observationIndex < trace.observations.length - 1
                ? `Advance from ${config.natural.radix}^${observation.power}`
                : 'Decide Less Than',
            description: observation.relation === 'equal' && observationIndex < trace.observations.length - 1
                ? `Both operands match at power ${observation.power}; clear failed fits before continuing to the next lower band.`
                : `Compare Source and Target arithmetically at power ${observation.power}; a Source mismatch does not settle Target. LessThan is ${trace.decision}.`,
            keyframes,
        });
        if (decision !== undefined) break;
    }

    const completed = snapshot();
    operandOpacity = config.natural.display.band.spentOpacity;
    steps.push({
        ...momentKey(stepMoment('complete')),
        label: 'Complete',
        description: 'The Boolean result is resolved from the highest-power-first comparison trace.',
        keyframes: [completed, snapshot(config.natural.animation?.fadeDurationMs ?? 0)],
    });
    return steps;
}
